import { Link, useParams } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import {
  useDaftarTenant, useFiturTenantOleh, useGrup, useJalurMasuk, useKoneksi,
  useModelTenantOleh, useOrangTenant, usePaguOleh, usePengetahuan,
} from '../../../domains/tenancy/api'
import { tanggal } from '../../../shared/lib/waktu'
import {
  Baris, Bendera, Chip, Gagal, KepalaHalaman, Memuat, Panel, Tabel,
} from '../../../shared/ui'

/**
 * ONBOARDING — tangga tujuh langkah dari tenant dibuat sampai diserahkan ke klien.
 *
 * KEADAAN TIAP LANGKAH DITURUNKAN, TIDAK DISIMPAN. Tidak ada tabel `onboarding_langkah`
 * dan halaman ini sengaja tidak menuntut satu pun: keadaan yang disimpan terpisah dari
 * kenyataan akan menyimpang darinya — centang "dokumen sudah masuk" tetap hijau sesudah
 * dokumennya dihapus, dan operator yang mempercayainya menyerahkan tenant yang botnya
 * tidak punya bahan. Yang dibaca di sini data yang MENGGERAKKAN sistem: ada berapa grup
 * aktif, berapa potongan lahir dari dokumennya, apakah webhook pernah benar-benar sampai.
 *
 * URUTANNYA DIBALIK DARI YANG ALAMI, DAN ITU DISENGAJA. "Hubungkan Instagram" ditaruh di
 * langkah 5, sesudah pengetahuan dan fitur beres — bukan di awal seperti yang biasanya
 * masuk akal. Sebabnya: `tenants.mode` punya nilai `draft` yang artinya "belum aktif",
 * tapi kolom itu NOL PEMBACA di jalur masuk. Begitu Instagram tersambung, bot langsung
 * melayani publik dengan pengetahuan apa pun yang kebetulan sudah terisi saat itu.
 * Kalau `mode` sudah ditegakkan nanti, urutan ini boleh dikembalikan.
 *
 * YANG TIDAK ADA DI SINI, DAN SEBABNYA:
 *
 *   "dimulai 2 Sep · terakhir disentuh 6 Sep" dan "diminta 5 September, belum dikirim"
 *   ada di rancangan dan TIDAK dibuat. Keduanya butuh tabel yang mencatat riwayat
 *   onboarding, dan tabel baru bukan keputusan yang diambil sambil membangun layar.
 *   Menampilkan tanggal karangan supaya layarnya terlihat lengkap justru merusak satu-
 *   satunya gunanya: mengetahui onboarding mana yang macet.
 *
 *   Tombol "Kirim tautan penghubungan", "Ingatkan lagi", "Unggah untuk mereka" juga
 *   tidak ada. Belum ada jalur yang mengirimkannya, dan tombol yang tidak melakukan apa-
 *   apa lebih buruk daripada tidak ada tombol — ia membuat orang mengira sudah mengirim.
 *   Yang dipasang di tiap langkah: tautan ke tempat pekerjaannya SUNGGUH dikerjakan.
 */

/** Satu anak tangga. `nomor` dipakai sebagai penanda, bukan sekadar hitungan urut. */
function Langkah({ nomor, judul, keadaan, anak, tautan, catatan, t, akhir }) {
  const NADA = { selesai: 'ok', sekarang: 'warn', menunggu: 'idle', terkunci: 'idle' }
  return (
    <li className="flex gap-3">
      {/* Garis penghubung digambar oleh anak tangga, bukan oleh <ol>: langkah terakhir
          tidak boleh punya ekor yang menggantung ke bawah tanpa tujuan. */}
      <div className="flex flex-col items-center shrink-0">
        <span
          className={[
            'w-6 h-6 rounded-full grid place-items-center shrink-0',
            'text-[11px] font-semibold tabular',
            keadaan === 'selesai' ? 'bg-ok text-white'
              : keadaan === 'sekarang' ? 'bg-accent text-white'
              : 'bg-surface2 text-ink3 border border-line',
          ].join(' ')}
        >
          {keadaan === 'selesai' ? '✓' : nomor}
        </span>
        {!akhir && <span className="w-px flex-1 bg-line mt-1" />}
      </div>

      <div className="flex-1 min-w-0 pb-5">
        <h3 className="m-0 flex flex-wrap items-center gap-2 text-[13.5px] font-semibold">
          {judul}
          <Chip nada={NADA[keadaan]}>{t(`ob.keadaan.${keadaan}`)}</Chip>
        </h3>
        {catatan && (
          <p className="mt-1 mb-0 text-[12px] text-ink2 max-w-[76ch]">{catatan}</p>
        )}
        {anak}
        {tautan && (
          <div className="mt-2.5">
            <Link
              to={tautan.ke}
              className="inline-block text-[11px] font-semibold no-underline
                         rounded border px-2 py-1 bg-surface text-ink2
                         border-lineStrong hover:text-ink hover:border-ink3"
            >
              {tautan.label} →
            </Link>
          </div>
        )}
      </div>
    </li>
  )
}

/** Tabel kecil "yang dibutuhkan / sudah?" di dalam satu langkah. */
function Syarat({ baris, t }) {
  return (
    <div className="mt-2.5 border border-line rounded overflow-hidden">
      <Tabel kepala={[t('ob.kolDibutuhkan'), t('ob.kolSudah')]}>
        {baris.map((b) => (
          <Baris key={b.nama}>
            <td>{b.nama}</td>
            <td><Chip nada={b.ok ? 'ok' : 'bad'}>{b.isi}</Chip></td>
          </Baris>
        ))}
      </Tabel>
    </div>
  )
}

export default function Onboarding() {
  const { id } = useParams()
  const { t, locale } = useBahasa()

  const daftar = useDaftarTenant()
  const koneksi = useKoneksi(id)
  const grup = useGrup(id)
  const orang = useOrangTenant(id)
  const dok = usePengetahuan(id)
  const fitur = useFiturTenantOleh(id)
  const model = useModelTenantOleh(id)
  const pagu = usePaguOleh(id)
  const jalur = useJalurMasuk({ tenant: id, limit: 50 })

  const semua = [daftar, koneksi, grup, orang, dok, fitur, model, jalur]
  if (semua.some((q) => q.isLoading)) return <Memuat tinggi={8} />
  const rusak = semua.find((q) => q.isError)
  if (rusak) return <Gagal galat={rusak.error} ulangi={rusak.refetch} />

  const tenant = (daftar.data ?? []).find((x) => x.id === id)
  const grupAktif = (grup.data ?? []).filter((g) => g.aktif)
  const tim = orang.data ?? []
  const tertaut = tim.filter((o) => o.telegram_user_id)
  // DOKUMEN DENGAN NOL POTONGAN TIDAK DIHITUNG. Ia ada di tabel, versinya benar, dan bot
  // tidak bisa menyentuhnya sama sekali — menghitungnya sebagai "pengetahuan sudah masuk"
  // adalah persis kebohongan yang membuat tenant diserahkan dengan bot yang mengarang.
  const dokTerpakai = (dok.data ?? []).filter((d) => d.is_active && d.potongan > 0)
  const fiturHidup = (fitur.data ?? []).filter((f) => f.enabled)
  const akunAktif = (koneksi.data?.akun ?? []).filter((a) => a.aktif ?? a.is_active ?? true)
  // Webhook yang PERNAH SAMPAI — bukan yang dikirim. Hanya ini yang membuktikan rantai
  // Meta → nginx → API benar-benar tersambung untuk tenant ini.
  const masuk = (jalur.data?.items ?? jalur.data ?? [])
    .filter((e) => e.status === 'diterima' || e.status === 'diproses')
  const adminMasuk = tim.filter((o) => o.role === 'tenant_admin' && o.last_login_at)

  const selesai = {
    1: Boolean(tenant),
    2: grupAktif.length > 0 && tim.length > 0 && tertaut.length > 0,
    3: dokTerpakai.length > 0,
    4: fiturHidup.length > 0 && (model.data ?? []).length > 0,
    5: Boolean(koneksi.data?.siap) && akunAktif.length > 0,
    6: masuk.length > 0,
    7: adminMasuk.length > 0,
  }
  // Langkah 6 menuntut 3 DAN 5; langkah 7 menuntut 6. Bukan hiasan: DM uji yang dikirim
  // sebelum dokumennya masuk akan dijawab bot yang tidak punya bahan, dan hasilnya
  // terbaca sebagai "botnya jelek" alih-alih "belum diberi apa-apa".
  const urut = [1, 2, 3, 4, 5, 6, 7]

  // KUNCINYA MERAMBAT. Langkah 7 menunggu 6, dan 6 menunggu 3 dan 5 — jadi selama 3
  // belum beres, 7 ikut terkunci walau syarat-syaratnya sendiri sudah terpenuhi.
  // Membaca `selesai[6]` mentah di sini membuka langkah 7 lewat langkah 6 yang tidak
  // pernah sah dijalankan, dan tenantnya terlihat siap diserahkan.
  const terkunci = { 6: !(selesai[3] && selesai[5]) }
  terkunci[7] = terkunci[6] || !selesai[6]

  // LANGKAH TERKUNCI TIDAK PERNAH DIHITUNG SELESAI, walau datanya kebetulan ada.
  //
  // Bukan kerapian tampilan. Langkah 6 "uji dengan DM sungguhan" jadi hijau begitu satu
  // webhook sampai — dan webhook bisa sampai sebelum dokumen pengetahuan masuk. Ujinya
  // lalu dijawab bot yang tidak punya bahan, jadi yang terbukti bukan "rantainya benar"
  // melainkan "ada pesan lewat". Menghitungnya menjadikan tenant terlihat 6 dari 7 dan
  // siap diserahkan, padahal satu-satunya langkah yang membuktikan ia bekerja belum
  // pernah dijalankan dengan benar. Hal sama untuk langkah 7 terhadap 6.
  const tuntas = (n) => selesai[n] && !terkunci[n]
  const sekarang = urut.find((n) => !tuntas(n) && !terkunci[n])
  const sudah = urut.filter(tuntas).length

  const keadaan = (n) => (
    terkunci[n] ? 'terkunci'
      : selesai[n] ? 'selesai'
      : n === sekarang ? 'sekarang'
      : 'menunggu'
  )

  return (
    <>
      <KepalaHalaman
        judul={t('ob.judul', { nama: tenant?.name ?? '—' })}
        keterangan={t('ob.keterangan')}
        aksi={
          <Link
            to={`/tenant/${id}`}
            className="text-[12px] font-semibold no-underline rounded border
                       px-2.5 py-1.5 bg-surface text-ink2 border-lineStrong
                       hover:text-ink hover:border-ink3"
          >
            {t('ob.bukaDetail')}
          </Link>
        }
      />

      <Panel judul={t('ob.majuJudul')} catatan={t('ob.majuCatatan')}>
        <div className="px-3.5 py-3 flex items-center gap-3">
          <span className="flex-1 h-1.5 bg-surface2 rounded overflow-hidden">
            <span
              className="block h-full bg-accent rounded"
              style={{ width: `${Math.round((sudah / urut.length) * 100)}%` }}
            />
          </span>
          <b className="text-[12.5px] tabular whitespace-nowrap">
            {t('ob.dariTujuh', { n: sudah })}
          </b>
        </div>
      </Panel>

      <Bendera nada="bad" judul={t('ob.urutanJudul')} className="max-w-[80ch]">
        {t('ob.urutanIsi')}
      </Bendera>

      <Panel judul={t('ob.tanggaJudul')} catatan={t('ob.tanggaCatatan')}>
        <ol className="list-none m-0 p-3.5 flex flex-col">
          <Langkah
            nomor={1} t={t} keadaan={keadaan(1)} judul={t('ob.l1')}
            catatan={tenant
              ? t('ob.l1Isi', {
                  mode: tenant.mode,
                  zona: tenant.timezone,
                  tanggal: tanggal(tenant.created_at, locale),
                })
              : t('ob.l1Hilang')}
          />

          <Langkah
            nomor={2} t={t} keadaan={keadaan(2)} judul={t('ob.l2')}
            catatan={t('ob.l2Isi')}
            tautan={{ ke: `/tenant/${id}`, label: t('ob.keSambungan') }}
            anak={
              <Syarat
                t={t}
                baris={[
                  { nama: t('ob.sGrup'), ok: grupAktif.length > 0,
                    isi: grupAktif.length > 0
                      ? t('ob.nGrup', { n: grupAktif.length })
                      : t('ob.belumAda') },
                  { nama: t('ob.sOrang'), ok: tim.length > 0,
                    isi: tim.length > 0
                      ? t('ob.nOrang', { n: tim.length })
                      : t('ob.belumAda') },
                  { nama: t('ob.sTertaut'), ok: tertaut.length > 0,
                    isi: t('ob.nTertaut', { n: tertaut.length, total: tim.length }) },
                ]}
              />
            }
          />

          <Langkah
            nomor={3} t={t} keadaan={keadaan(3)} judul={t('ob.l3')}
            catatan={t('ob.l3Isi')}
            tautan={{ ke: `/tenant/${id}`, label: t('ob.kePengetahuan') }}
            anak={
              <Syarat
                t={t}
                baris={[
                  { nama: t('ob.sDokumen'), ok: dokTerpakai.length > 0,
                    isi: t('ob.nDokumen', {
                      n: dokTerpakai.length, total: (dok.data ?? []).length,
                    }) },
                ]}
              />
            }
          />

          <Langkah
            nomor={4} t={t} keadaan={keadaan(4)} judul={t('ob.l4')}
            catatan={t('ob.l4Isi')}
            tautan={{ ke: `/tenant/${id}`, label: t('ob.keFitur') }}
            anak={
              <Syarat
                t={t}
                baris={[
                  { nama: t('ob.sFitur'), ok: fiturHidup.length > 0,
                    isi: t('ob.nFitur', {
                      n: fiturHidup.length, total: (fitur.data ?? []).length,
                    }) },
                  { nama: t('ob.sModel'), ok: (model.data ?? []).length > 0,
                    isi: (model.data ?? []).length > 0
                      ? t('ob.nModel', { n: model.data.length })
                      : t('ob.pakaiBawaan') },
                  // PAGU TIDAK MENENTUKAN LANGKAH INI SELESAI. Tenant tanpa pagu sah —
                  // ia cuma tidak dibatasi. Menuntutnya akan menahan onboarding demi
                  // angka yang memang belum tentu ada.
                  { nama: t('ob.sPagu'), ok: pagu.data?.pagu_bulanan_usd != null,
                    isi: pagu.data?.pagu_bulanan_usd != null
                      ? `$${pagu.data.pagu_bulanan_usd}`
                      : t('ob.tanpaPaguTakApa') },
                ]}
              />
            }
          />

          <Langkah
            nomor={5} t={t} keadaan={keadaan(5)}
            judul={
              <>
                {t('ob.l5')}
                <Chip nada="bad">{t('ob.takBisaKembali')}</Chip>
              </>
            }
            catatan={t('ob.l5Isi')}
            tautan={{ ke: `/tenant/${id}`, label: t('ob.keSambungan') }}
            anak={
              (koneksi.data?.kurang ?? []).length > 0 ? (
                <Syarat
                  t={t}
                  baris={(koneksi.data.kurang).map((k) => (
                    { nama: k, ok: false, isi: t('ob.belumAda') }
                  ))}
                />
              ) : null
            }
          />

          <Langkah
            nomor={6} t={t} keadaan={keadaan(6)} judul={t('ob.l6')}
            catatan={terkunci[6] ? t('ob.l6Terkunci') : t('ob.l6Isi')}
            tautan={{ ke: '/jalur-masuk', label: t('ob.keJalur') }}
            anak={
              <Syarat
                t={t}
                baris={[
                  { nama: t('ob.sWebhook'), ok: masuk.length > 0,
                    isi: masuk.length > 0
                      ? t('ob.nWebhook', { n: masuk.length })
                      : t('ob.belumPernahSampai') },
                ]}
              />
            }
          />

          <Langkah
            nomor={7} t={t} keadaan={keadaan(7)} judul={t('ob.l7')} akhir
            catatan={terkunci[7] ? t('ob.l7Terkunci') : t('ob.l7Isi')}
            anak={
              <Syarat
                t={t}
                baris={[
                  { nama: t('ob.sAdminMasuk'), ok: adminMasuk.length > 0,
                    isi: adminMasuk.length > 0
                      ? t('ob.nAdminMasuk', { n: adminMasuk.length })
                      : t('ob.belumPernahMasuk') },
                ]}
              />
            }
          />
        </ol>
      </Panel>

      <Bendera nada="info" judul={t('ob.menungguJudul')} className="max-w-[80ch]">
        {t('ob.menungguIsi')}
      </Bendera>

      <Bendera nada="info" judul={t('ob.turunanJudul')} className="max-w-[80ch]">
        {t('ob.turunanIsi')}
      </Bendera>
    </>
  )
}
